import { ISearchProduct } from '@/types';
import { sanitizeContent } from '@/lib/sanitize-content';
import Tabs from '@/components/ui/tabs';
import cn from 'classnames';
import { useState } from 'react';

const EXCLUDED_CHARACTERISTIC_IDS = [10, 14, 15, 16, 17, 19, 31, 35, 36];

function formatCharacteristicValue(value: unknown): string {
  if (value === true || value === 'true') return 'Sim';
  if (value === false || value === 'false') return 'Não';
  return String(value ?? '');
}

interface ProductInfosProps {
  product: ISearchProduct;
  characteristics?: object | undefined;
}

const DescriptionContentTableItem = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <div
      className={`flex items-center border-gray-200 pt-4 pb-4 md:border-t-0`}
    >
      {children}
    </div>
  );
};

const DescriptionContentTableItemValue = ({
  bold,
  right,
  children,
  id,
  className,
}: {
  bold?: boolean;
  right?: boolean;
  children: React.ReactNode;
  id: string;
  className?: string;
}) => {
  return (
    <div
      id={id}
      className={cn(
        'flex-1 mb-0 text-base break-words whitespace-pre-wrap',
        {
          'font-bold text-accent': bold,
          'font-normal text-gray-800': !bold,
          'text-right': right,
          'text-left md:text-left': !right,
        },
        className,
      )}
      dangerouslySetInnerHTML={{ __html: children as string }}
    ></div>
  );
};

export default function ProductInfos({
  product,
  characteristics = {},
}: Readonly<ProductInfosProps>) {
  const { sku } = product ?? {};

  const tabs = Object.keys(characteristics).map((key) => {
    const group = characteristics[key];
    return {
      id: group.id,
      name: group.name,
      characteristics: group.characteristics,
    };
  });

  const [activeTab, setActiveTab] = useState(0);

  const sanitizedTabs = tabs.map((tab) => ({
    ...tab,
    characteristics: tab.characteristics.map((item: any) => ({
      ...item,
      sanitizedValue: sanitizeContent(item.value),
    })),
  }));

  return (
    <Tabs
      tabs={tabs.map((tab) => tab.name)}
      onTabChange={setActiveTab}
      contents={sanitizedTabs.map((item: any, index: number) => {
        const isDescriptionTabActive = activeTab === index;

        if (item.name.includes('Descrição')) {
          return item.characteristics
            .filter((char: any) => char.id !== 29)
            .map((item: any) => {
              return (
                <div key={item.id} className="mb-4">
                  <div
                    className="react-editor-description product-description py-4"
                    dangerouslySetInnerHTML={{ __html: item.sanitizedValue }}
                  />
                </div>
              );
            });
        }


        const filteredCharacteristics = item.characteristics.filter(
          (char: { id: number }) => !EXCLUDED_CHARACTERISTIC_IDS.includes(char.id),
        );
        const characteristicsWithSku = [
          {
            id: 'sku',
            name: 'Cód. Interno',
            value: sku,
          },
          ...filteredCharacteristics,
        ];

        return (
          <div
            className={cn(
              'flex flex-col gap-1 w-full transition-all duration-300',
              {
                'h-0 overflow-hidden': !isDescriptionTabActive,
                'h-auto': isDescriptionTabActive,
              },
            )}
            id="product-details-container"
            key={item.id}
          >
            {characteristicsWithSku.map((item: any) => {
              const { name, value } = item;

              const displayValue = formatCharacteristicValue(value);
              if (!displayValue) return;

              return (
                <div key={name} className={cn('w-full')}>
                  <DescriptionContentTableItem key={name}>
                    <DescriptionContentTableItemValue
                      bold
                      id="product-detail-label"
                    >
                      {name}
                    </DescriptionContentTableItemValue>
                    <DescriptionContentTableItemValue
                      id="product-detail-value"
                      className="truncate md:whitespace-normal"
                    >
                      {displayValue}
                    </DescriptionContentTableItemValue>
                  </DescriptionContentTableItem>
                </div>
              );
            })}
          </div>
        );
      })}
    />
  );
}
